// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ComplexArticleBlueprintDTO } from '@admintool/article/generate-article/complex-article-blueprints/complex-article-blueprint';
import { ComplexArticleBlueprintPropertiesComponent } from '@admintool/article/generate-article/complex-article-blueprints/complex-article-blueprint-properties/complex-article-blueprint-properties.component';
import { ComplexArticleBlueprintsComponent } from '@admintool/article/generate-article/complex-article-blueprints/complex-article-blueprints.component';
import { ArticleVersionsComponent } from '@admintool/components/article/article-versions/article-versions.component';
import { EditArticleMaterialsComponent } from '@admintool/components/article/edit-article-materials/edit-article-materials.component';
import { SelectFromMultipleFoundArticlesComponent } from '@admintool/components/select-from-multiple-found-articles/select-from-multiple-found-articles.component';
import { ArticleMetadataVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleVersionComponent } from '../article/version/article-version.component';
import { ArticleEndPlateInfoVO } from '@article/model/vo/article-end-plate-info-vo';
import { EditEndplateComponent } from '../article/endplate/edit-endplate/edit-endplate.component';
import { AddEndplateComponent } from '../article/endplate/add-endplate/add-endplate.component';
import { PropertyNames, PropertyTemplateVO } from '@project/components/article/model/vo/property-template-vo';
import { AddEditPropertyDefComponent } from '../property/add-edit-property-def/add-edit-property-def.component';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { EditPropertyModalComponent } from '../property/edit-property/edit-property-modal.component';
import { MetaElementVO } from '@shared/vo/meta-element.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ConnectionCreationParameters } from '@article/components/connections/connection-creation/connection-creator';
import { NewConnectionModalComponent } from '@admintool/right-panel/connection/add-connection/new-connection-modal.component';
import { DuplicatedOrderNumbersComponent } from '@admintool/dialogs/duplicated-order-numbers/duplicated-order-numbers.component';
import { ImportArticleComponent } from '@admintool/article/import-article/import-article.component';
import { ManageArticleMetaInfoComponent } from '../article/manage-article-meta-info/manage-article-meta-info.component';
import { WindowService } from '@shared/window/window.service';
import { NewTextureElementPopupComponent, TextureElementCreationData } from '../../dialogs/add-texture-element/new-texture-element-popup.component';
import { UndoRedoHistoryComponent } from '../../admintool-scene/admintool-toolbar/undo-redo-history/undo-redo-history.component';
import { MoveCoordinateCenterComponent } from '../article/move-coordinate-center/move-coordinate-center.component';
import { PositionAndRotation } from '@3d-scene/model3d/global-container';
import { InputSeriesModalComponent } from '../../dialogs/input-series/input-series-modal.component';

@Injectable({providedIn: 'root' })
export class AdmintoolComponentsPresenter {

  constructor(
    private windowService: WindowService
  ) {}

  public openArticleVersionDialog(articleVO: ArticleVO): Promise<boolean> {
    return this.windowService.showModalMdFinish(ArticleVersionComponent,{articleVO});
  }

  public openEditEndPlateInfoPopup(endPlate: ArticleEndPlateInfoVO): Promise<boolean> {
    return this.windowService.showModalMdFinish(EditEndplateComponent, {endPlate});
  }

  public openAddEndPlatePopup(): Promise<ArticleEndPlateInfoVO> {
    return this.windowService.showModalMdFinish(AddEndplateComponent);
  }

  public openEditPropertyDefManagementDialog(property: PropertyTemplateVO, propertiesNames: PropertyNames): Promise<void> {
    return this.windowService.showModalMdFinish(AddEditPropertyDefComponent, { property, propertiesNames});
  }

  public openEditArticleMaterials(): void {
    this.windowService.showModal(EditArticleMaterialsComponent, {keyboard: true});
  }

  public async openMoveCoordinateCenter(positionAndRotation: PositionAndRotation): Promise<{new: number, old: number}[]> {
    return this.windowService.showModalMdFinish(MoveCoordinateCenterComponent, { positionAndRotation });
  }

  public openAddPropertyDefManagementDialog(propertiesNames: PropertyNames, initiallySelectedPropertyType: string): Promise<void> {
    return this.windowService.showModalFzFinish(AddEditPropertyDefComponent, { propertiesNames, initiallySelectedPropertyType });
  }

  public showEditPropertyPopup(property: PropertyVO | undefined, propertyType: string | undefined, connection?: MetaElementVO, article?: ArticleVO): Promise<boolean> {
    return this.windowService.showModalMdFinish(EditPropertyModalComponent, { property, propertyType, metaElement: connection, article });
  }

  public openDuplicatedOrderNumbersDialog(): Promise<boolean> {
    return this.windowService.showModalMdFinish(DuplicatedOrderNumbersComponent);
  }

  public showNewConnectionPopup(
    articleVO: ArticleVO,
    articleContainer: ArticleContainer,
    connectionParameters?: ConnectionCreationParameters
  ): Promise<any> {
    return this.windowService.showModalMdFinish(
      NewConnectionModalComponent,
      {
        articleVO,
        articleContainer,
        connectionParameters
      });
  }

  public openImportArticleDialog(articleVO: ArticleVO): Promise<void> {
    return this.windowService.showModalMdFinish(ImportArticleComponent, { articleVO });
  }

  public openManageArticleMetaInfoDialog(articleId: number, articleMetaData: ArticleMetadataVO = null): Promise<ArticleMetadataVO> {
    return this.windowService.showModalMdFinish(ManageArticleMetaInfoComponent, { articleId, articleMetaData });
  }

  public showNewTextureElementPopup(article: ArticleVO): Promise<TextureElementCreationData> {
    return this.windowService.showModalMdFinish(NewTextureElementPopupComponent, { article });
  }

  public selectFromMultipleFoundArticles(articles: ArticleVO[]): Promise<ArticleVO> {
    return this.windowService.showModalMdFinish(SelectFromMultipleFoundArticlesComponent, { articles });

  }

  public showUndoRedoHistory(operationName: string) {
    this.windowService.showModal(UndoRedoHistoryComponent, {
      class: 'custom-dropdown',
      initialState: {
        operationType: operationName,
      },
    });
  }

  public async showArticleVersionsComponent(articleVo: ArticleVO): Promise<void> {
    await this.windowService.showModalMdFinish(ArticleVersionsComponent, { articleVo, currentVersion: articleVo.version });
  }

  public openInputSeriesDialog(): Promise<string | undefined> {
    return this.windowService.showModalMdFinish(InputSeriesModalComponent);
  }

  public showComplexArticleBlueprintsModal(): void {
    this.windowService.showModal(ComplexArticleBlueprintsComponent, {class: 'modal-lg', ignoreBackdropClick: true});
  }

  public showEditComplexArticleRulesModal(blueprint: ComplexArticleBlueprintDTO): Promise<ComplexArticleBlueprintDTO> {
    return this.windowService.showModalFinish(ComplexArticleBlueprintPropertiesComponent, {initialState: {blueprint}, ignoreBackdropClick: true, class: 'modal-lg'});
  }
}
